import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useColorModeValue,
  Box,
  VStack,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Text,
  Badge,
  Progress,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import UserDetailModalContent from '../components/UserDetailModalContent';

const UserDashboardModal = ({ isOpen, onClose, userData }) => {
  const modalContentBg = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const chartColors = ['#4299E1', '#48BB78', '#ED8936', '#E53E3E'];

  if (!userData) return null;

  // Sample data for charts - replace with actual data from userData
  const tokenUsageData = [
    { name: 'Jan', tokens: 1000 },
    { name: 'Feb', tokens: 1200 },
    { name: 'Mar', tokens: 900 },
    { name: 'Apr', tokens: 1500 },
    { name: 'May', tokens: 1800 },
    { name: 'Jun', tokens: 2000 },
  ];

  const agentUsageData = [
    { name: 'Chat', value: userData.agents?.chat?.total || 0 },
    { name: 'Voice', value: userData.agents?.voice?.total || 0 },
  ];

  const monthlyUsageData = [
    { name: 'Week 1', chat: 400, voice: 240 },
    { name: 'Week 2', chat: 300, voice: 139 },
    { name: 'Week 3', chat: 200, voice: 980 },
    { name: 'Week 4', chat: 278, voice: 390 },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        maxW={{ base: '95%', md: '80%', lg: '1000px' }}
        minH={{ base: '80vh', md: '70vh' }}
        maxH="90vh"
        bg={modalContentBg}
      >
        <ModalHeader borderBottom="1px solid" borderColor={useColorModeValue('gray.200', 'gray.700')}>
           User Details: {userData.username}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={4}>
          <Tabs>
            <TabList>
              <Tab>Overview</Tab>
              <Tab>Agents</Tab>
              <Tab>Billing</Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  {/* Token Usage Stats with Chart */}
                  <Card bg={cardBg} boxShadow="md">
                    <CardHeader>
                      <Text fontSize="lg" fontWeight="bold">Token Usage</Text>
                    </CardHeader>
                    <CardBody>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                        <Box>
                          <Stat>
                            <StatLabel>Total Tokens</StatLabel>
                            <StatNumber>{userData.tokens?.total || 0}</StatNumber>
                            <StatHelpText>
                              <StatArrow type="increase" />
                              {userData.tokens?.increase || 0}%
                            </StatHelpText>
                          </Stat>
                          <Stat mt={4}>
                            <StatLabel>Remaining Tokens</StatLabel>
                            <StatNumber>{userData.tokens?.remaining || 0}</StatNumber>
                            <Progress value={(userData.tokens?.remaining / userData.tokens?.total) * 100} colorScheme="blue" />
                          </Stat>
                        </Box>
                        <Box h="300px">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={tokenUsageData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              <Line type="monotone" dataKey="tokens" stroke="#4299E1" />
                            </LineChart>
                          </ResponsiveContainer>
                        </Box>
                      </SimpleGrid>
                    </CardBody>
                  </Card>

                  {/* Agent Usage Distribution */}
                  <Card bg={cardBg} boxShadow="md">
                    <CardHeader>
                      <Text fontSize="lg" fontWeight="bold">Agent Usage Distribution</Text>
                    </CardHeader>
                    <CardBody>
                      <Box h="300px">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={agentUsageData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {agentUsageData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                    </CardBody>
                  </Card>
                </VStack>
              </TabPanel>

              <TabPanel>
                <VStack spacing={6} align="stretch">
                  {/* Monthly Usage Comparison Chart */}
                  <Card bg={cardBg} boxShadow="md">
                    <CardHeader>
                      <Text fontSize="lg" fontWeight="bold">Monthly Usage Comparison</Text>
                    </CardHeader>
                    <CardBody>
                      <Box h="400px">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={monthlyUsageData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="chat" fill="#4299E1" name="Chat Interactions" />
                            <Bar dataKey="voice" fill="#48BB78" name="Voice Minutes" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </CardBody>
                  </Card>

                  {/* Agent Statistics Cards */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <Card bg={cardBg} boxShadow="md">
                      <CardHeader>
                        <Text fontSize="lg" fontWeight="bold">Chat Bot Agent Statistics</Text>
                      </CardHeader>
                      <CardBody>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                          <Stat>
                            <StatLabel>Active Sessions</StatLabel>
                            <StatNumber>{userData.agents?.chat?.active || 0}</StatNumber>
                          </Stat>
                          <Stat>
                            <StatLabel>Success Rate</StatLabel>
                            <StatNumber>{userData.agents?.chat?.successRate || '0%'}</StatNumber>
                          </Stat>
                          <Stat>
                            <StatLabel>Average Response Time</StatLabel>
                            <StatNumber>{userData.agents?.chat?.avgResponseTime || '0s'}</StatNumber>
                          </Stat>
                        </SimpleGrid>
                      </CardBody>
                    </Card>

                    <Card bg={cardBg} boxShadow="md">
                      <CardHeader>
                        <Text fontSize="lg" fontWeight="bold">Voice Agent Statistics</Text>
                      </CardHeader>
                      <CardBody>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                          <Stat>
                            <StatLabel>Active Calls</StatLabel>
                            <StatNumber>{userData.agents?.voice?.active || 0}</StatNumber>
                          </Stat>
                          <Stat>
                            <StatLabel>Call Duration</StatLabel>
                            <StatNumber>{userData.agents?.voice?.totalDuration || '0m'}</StatNumber>
                          </Stat>
                          <Stat>
                            <StatLabel>Quality Score</StatLabel>
                            <StatNumber>{userData.agents?.voice?.qualityScore || '0%'}</StatNumber>
                          </Stat>
                        </SimpleGrid>
                      </CardBody>
                    </Card>
                  </SimpleGrid>
                </VStack>
              </TabPanel>

              <TabPanel>
                <VStack spacing={6} align="stretch">
                  {/* Billing Information with Usage Chart */}
                  <Card bg={cardBg} boxShadow="md">
                    <CardHeader>
                      <Text fontSize="lg" fontWeight="bold">Billing Overview</Text>
                    </CardHeader>
                    <CardBody>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                        <Box>
                          <Text fontWeight="bold">Current Plan</Text>
                          <Badge colorScheme="green" fontSize="md" p={2} borderRadius="md">
                            {userData.billing?.plan || 'Basic'}
                          </Badge>
                          <Text mt={2} fontWeight="bold">Next Billing Date</Text>
                          <Text>{userData.billing?.nextBillingDate || 'N/A'}</Text>
                        </Box>
                        <Box>
                          <Text fontWeight="bold">Usage This Month</Text>
                          <Text>Voice Minutes: {userData.billing?.monthlyVoiceMinutes || 0}</Text>
                          <Text>Chat Tokens: {userData.billing?.monthlyChatTokens || 0}</Text>
                          <Text mt={2} fontWeight="bold">Estimated Cost</Text>
                          <Text>${userData.billing?.estimatedCost || 0}</Text>
                        </Box>
                      </SimpleGrid>
                    </CardBody>
                  </Card>

                  {/* Usage Trend Chart */}
                  <Card bg={cardBg} boxShadow="md">
                    <CardHeader>
                      <Text fontSize="lg" fontWeight="bold">Usage Trend</Text>
                    </CardHeader>
                    <CardBody>
                      <Box h="300px">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={monthlyUsageData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="chat" stroke="#4299E1" name="Chat Usage" />
                            <Line type="monotone" dataKey="voice" stroke="#48BB78" name="Voice Usage" />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    </CardBody>
                  </Card>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default UserDashboardModal; 